import React, { useState, useRef, useEffect } from 'react';

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  options: Option[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  onConfirm?: (value: string | string[]) => void;
  placeholder?: string;
  multiple?: boolean;
  allowCustomValue?: boolean;
  required?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  onConfirm,
  placeholder = 'Select...',
  multiple = false,
  allowCustomValue = false,
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const filtered = options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
    
    if (allowCustomValue && !multiple) {
      onChange(e.target.value);
    }
  };

  const handleOptionClick = (optionValue: string) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue];
      onConfirm?.(newValues);
    } else {
      onConfirm?.(optionValue);
      setSearchTerm(optionValue);
      setIsOpen(false);
    }
  };

  const displayValue = () => {
    if (multiple && Array.isArray(value)) {
      return value.join(', ');
    }
    return typeof value === 'string' ? value : '';
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <input
          type="text"
          className="w-full px-3 py-2 border rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
          value={searchTerm || displayValue()}
          onChange={handleInputChange}
          onClick={() => setIsOpen(true)}
          placeholder={placeholder}
          required={required}
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 flex items-center px-2"
          onClick={() => setIsOpen(!isOpen)}
        >
          <svg className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredOptions.map((option) => (
            <div
              key={option.value}
              className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${
                multiple && Array.isArray(value) && value.includes(option.value)
                  ? 'bg-blue-100'
                  : value === option.value
                  ? 'bg-blue-100'
                  : ''
              }`}
              onClick={() => handleOptionClick(option.value)}
            >
              {multiple && (
                <input
                  type="checkbox"
                  checked={Array.isArray(value) && value.includes(option.value)}
                  onChange={() => {}}
                  className="mr-2"
                />
              )}
              {option.label}
            </div>
          ))}
          {allowCustomValue && searchTerm && !filteredOptions.some(opt => opt.label === searchTerm) && (
            <div
              className="px-3 py-2 cursor-pointer hover:bg-blue-50 italic"
              onClick={() => {
                onConfirm?.(searchTerm);
                setIsOpen(false);
              }}
            >
              Add "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomSelect; 